import React, { useCallback } from 'react';
import DateTimePicker from '../../components/general/DateTimePicker';
import { ThemeProvider } from '@mui/material';
import { theme } from '../../components/ui-kit/helpers';
import { Row, Col } from '../../components/ui-kit';
import { debounce } from 'lodash';
import { startOfMinute } from '../../utils/time';
import moment from 'moment';

const PeriodSelector = props => {
  const timeJump = props.defaultTimeJump || 2;

  const handleChange = useCallback(
    (startTime, endTime) => {
      startTime = startOfMinute(moment(startTime));
      endTime = endTime === null ? null : startOfMinute(moment(endTime));

      if (endTime === null && !props.allowOpenEnded) endTime = startTime.add(timeJump, 'hours');

      // If the date is not valid, don't update the field
      if (startTime && !startTime.isValid()) return;
      if (endTime && !endTime.isValid()) return;

      fixEndTimeDebounced(startTime, endTime);

      props.onChange({
        start_time: startTime.format(),
        end_time: endTime ? endTime.format() : endTime
      });
    },
    [props.startTime]
  );

  const fixEndTime = (startTime, endTime) => {
    // An overrideFixEndTime can be passed if the default behavior is not enough
    // to cover the needs of the parent component
    if (props.calculateEndTime) return props.calculateEndTime(startTime, endTime);

    // If the new start time is after the end time, we automatically update the
    // end time to be after the start time. The amount of hours it jumps ahead
    // are configurable
    if (endTime && startTime.isAfter(endTime)) {
      endTime = startTime.clone().add(timeJump, 'hours');

      props.onChange({
        start_time: startTime.format(),
        end_time: endTime ? endTime.format() : endTime
      });
    }
  };

  // give the user some time to finish typing before we update the end time. For
  // example if the start time is 12:20 and the user types 12:26, we don't want
  // to immediately update the end time to 14:02, when they have typed "12:2"
  const fixEndTimeDebounced = useCallback(debounce(fixEndTime, 700), [props.startTime]);

  const onChangeStartTime = useCallback(
    value => {
      const startTime = startOfMinute(moment(value));
      handleChange(startTime, props.endTime);
    },
    [props.endTime]
  );

  const onChangeEndTime = useCallback(
    value => {
      const endTime = value === null && props.allowOpenEnded ? null : startOfMinute(moment(value));
      handleChange(props.startTime, endTime);
    },
    [props.startTime]
  );

  if (!props.startTime) return null;

  const cols = props.horizontal ? 12 : 1;

  return (
    <ThemeProvider theme={theme}>
      <Row xs={cols} sm={cols} md={cols} lg={cols} xl={cols} className="period-selector">
        <Col className="mb-1">
          <DateTimePicker
            name="startTime"
            value={props.startTime}
            onChange={onChangeStartTime}
            minutesInterval={props.minutesInterval}
          />
        </Col>
        <Col>
          <DateTimePicker
            name="endTime"
            value={props.endTime}
            allowOpenEnded={props.allowOpenEnded}
            minDateTime={props.startTime}
            onChange={onChangeEndTime}
            minutesInterval={props.minutesInterval}
          />
        </Col>
      </Row>
    </ThemeProvider>
  );
};

export default PeriodSelector;
